// Data Table authoring UI (rendered inside the properties panel when a DataTable
// is selected). Edits columns / binding / style / totals; each change relays out
// the preview and pushes an undo state. The same controls produce the spec that
// report.ts renders — Liquid cell templates here are evaluated identically there.
//
// Restyled to the dark-slate theme (reuses `.prop-label` / `.prop-input` /
// `.prop-row` + safelisted utilities). Export signature + behaviour unchanged.
import {
  DataTable, addColumn, removeColumn, updateColumn, setTotals, setTableOption,
  setStaticMode, addStaticRow, removeStaticRow, setStaticCell,
  type DTColumn, type ColType, type CellKind, type Borders,
} from './../widgets/datatable'
import { icon } from './../ui/icons'

type Commit = () => void
type Rerender = () => void

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (cls) e.className = cls
  return e
}
function section(title: string): HTMLElement { const s = el('div', 'prop-label'); s.textContent = title; return s }
function row(label: string, control: HTMLElement): HTMLElement {
  const r = el('label', 'prop-row flex items-center gap-2')
  const l = el('span', 'prop-label text-[10px] text-slate-500'); l.textContent = label; l.style.minWidth = '70px'
  r.append(l, control); return r
}
function txt(value: string, placeholder: string, onCommit: (v: string) => void): HTMLInputElement {
  const i = el('input', 'prop-input flex-1'); i.type = 'text'; i.value = value; i.placeholder = placeholder
  i.addEventListener('change', () => onCommit(i.value)); return i
}
function num(value: number | undefined, onCommit: (v: number | undefined) => void): HTMLInputElement {
  const i = el('input', 'prop-input flex-1'); i.type = 'number'; i.value = value == null ? '' : String(value)
  i.addEventListener('change', () => onCommit(i.value === '' ? undefined : parseFloat(i.value) || 0)); return i
}
function sel(options: [string, string][], value: string, onChange: (v: string) => void): HTMLSelectElement {
  const s = el('select', 'prop-input flex-1')
  for (const [val, lbl] of options) { const o = el('option'); o.value = val; o.textContent = lbl; if (val === value) o.selected = true; s.appendChild(o) }
  s.addEventListener('change', () => onChange(s.value)); return s
}
function color(value: string, onCommit: (v: string) => void): HTMLInputElement {
  const i = el('input'); i.type = 'color'; i.value = value?.startsWith('#') ? value : '#111827'
  i.style.width = '32px'; i.style.height = '32px'; i.className = 'rounded border border-slate-600 cursor-pointer'
  i.style.background = 'transparent'
  i.addEventListener('change', () => onCommit(i.value)); return i
}
function check(value: boolean, onChange: (v: boolean) => void): HTMLInputElement {
  const i = el('input'); i.type = 'checkbox'; i.checked = value; i.style.accentColor = '#3b82f6'
  i.addEventListener('change', () => onChange(i.checked)); return i
}
/** A full-width slate button (e.g. "+ Column"). */
function wideBtn(iconName: string, label: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', 'tb-btn w-full justify-center text-[11px] gap-1'); b.type = 'button'
  b.innerHTML = icon(iconName, 'w-3.5 h-3.5') + `<span>${label}</span>`
  b.addEventListener('click', onClick); return b
}
/** A small icon-only delete button. */
function delBtn(onClick: () => void): HTMLButtonElement {
  const b = el('button', 'prop-icon-btn'); b.type = 'button'; b.title = 'Remove'
  b.innerHTML = icon('delete', 'w-3.5 h-3.5')
  b.style.color = '#f87171'
  b.addEventListener('click', onClick); return b
}

const COL_TYPES: [string, string][] = [['text', 'Text'], ['number', 'Number'], ['currency', 'Currency'], ['percent', 'Percent'], ['date', 'Date']]
const CELL_KINDS: [string, string][] = [['text', 'Text'], ['image', 'Image'], ['barcode', 'Barcode'], ['qr', 'QR']]
const ALIGNS: [string, string][] = [['left', 'Left'], ['center', 'Center'], ['right', 'Right']]
const BORDERS: [string, string][] = [['grid', 'Grid'], ['horizontal', 'Rows only'], ['none', 'None']]
const AGGS: [string, string][] = [['sum', 'Sum'], ['avg', 'Average'], ['count', 'Count']]

export function renderDataTablePanel(host: HTMLElement, t: DataTable, commit: Commit, rerender: Rerender): void {
  const stat = t.isStatic
  host.appendChild(section(stat ? 'Summary Table' : 'Data Table'))

  // Mode: data-bound (paginating) vs static/typed summary (cells are Liquid, root scope).
  const mode = el('div', 'space-y-2')
  mode.appendChild(row('Summary mode', check(stat, (on) => { setStaticMode(t, on); commit(); rerender() })))
  if (stat) {
    const hint = el('div', 'text-[10px] text-slate-500 px-1')
    hint.textContent = 'Typed table — cells accept Liquid against the data, e.g. {{ rows | where: "type","call" | sum: "charge" | money }}'
    mode.appendChild(hint)
  }
  host.appendChild(mode)

  // Binding: which dataset array the rows come from at render (data[path]; default `rows`).
  const opts = el('div', 'space-y-2')
  if (!stat) opts.appendChild(row('Data path', txt(t.binding?.path ?? '', 'rows', (v) => { setTableOption(t, { binding: v ? { path: v } : undefined }); commit() })))
  opts.appendChild(row('Borders', sel(BORDERS, t.borders, (v) => { setTableOption(t, { borders: v as Borders }); commit() })))
  opts.appendChild(row('Header fill', color(t.headFill, (v) => { setTableOption(t, { headFill: v }); commit() })))
  opts.appendChild(row('Alt row fill', color(t.altRowFill, (v) => { setTableOption(t, { altRowFill: v }); commit() })))
  opts.appendChild(row('Repeat header', check(t.repeatHeader, (v) => { setTableOption(t, { repeatHeader: v }); commit() })))
  opts.appendChild(row('Row height (pt)', num(t.rowHeightPt, (v) => { setTableOption(t, { rowHeightPt: v }); commit() })))
  opts.appendChild(row('Band font', txt(t.font ?? '', 'e.g. Roboto', (v) => { setTableOption(t, { font: v || undefined }); commit() })))
  host.appendChild(opts)

  // Columns
  host.appendChild(section('Columns'))
  const cols = el('div', 'space-y-2')
  t.columns.forEach((col: DTColumn, i: number) => {
    const card = el('div', 'rounded-lg border border-slate-700 bg-slate-800 space-y-2')
    card.style.padding = '8px'
    const head = el('div', 'flex items-center justify-between')
    const title = el('span', 'prop-label text-[11px] text-slate-300'); title.textContent = `${i + 1}. ${col.header || '(untitled)'}`
    head.append(title, delBtn(() => { removeColumn(t, i); commit(); rerender() }))
    card.appendChild(head)
    card.appendChild(row('Header', txt(col.header, 'Header', (v) => { updateColumn(t, i, { header: v }); commit(); rerender() })))
    if (!stat) {
      card.appendChild(row('Field', txt(col.field, 'csv column', (v) => { updateColumn(t, i, { field: v }); commit() })))
      card.appendChild(row('Cell (Liquid)', txt(col.cell ?? '', '{{ ' + (col.field || 'field') + ' | money }}', (v) => { updateColumn(t, i, { cell: v || undefined }); commit() })))
      card.appendChild(row('Type', sel(COL_TYPES, col.type ?? 'text', (v) => { updateColumn(t, i, { type: v as ColType }); commit() })))
      card.appendChild(row('Kind', sel(CELL_KINDS, col.cellKind ?? 'text', (v) => { updateColumn(t, i, { cellKind: v as CellKind }); commit() })))
    }
    card.appendChild(row('Align', sel(ALIGNS, col.align ?? 'left', (v) => { updateColumn(t, i, { align: v as DTColumn['align'] }); commit() })))
    card.appendChild(row('Width (pt)', num(col.width, (v) => { updateColumn(t, i, { width: v }); commit() })))
    cols.appendChild(card)
  })
  cols.appendChild(wideBtn('plus', 'Column', () => { addColumn(t); commit(); rerender() }))
  host.appendChild(cols)

  // Static mode → a typed grid of cells (each accepts Liquid); no data-bound totals.
  if (stat) {
    host.appendChild(section('Rows'))
    const grid = el('div', 'space-y-2')
    ;(t.staticRows ?? []).forEach((cells, r) => {
      const card = el('div', 'rounded-lg border border-slate-700 bg-slate-800 space-y-1')
      card.style.padding = '6px'
      const head = el('div', 'flex items-center justify-between')
      const title = el('span', 'prop-label text-[10px] text-slate-400'); title.textContent = `Row ${r + 1}`
      head.append(title, delBtn(() => { removeStaticRow(t, r); commit(); rerender() }))
      card.appendChild(head)
      t.columns.forEach((col, c) => {
        card.appendChild(row(col.header || `Col ${c + 1}`, txt(cells[c] ?? '', c === 0 ? 'Label' : '{{ rows | sum: "amount" | money }}', (v) => { setStaticCell(t, r, c, v); commit() })))
      })
      grid.appendChild(card)
    })
    grid.appendChild(wideBtn('plus', 'Row', () => { addStaticRow(t); commit(); rerender() }))
    host.appendChild(grid)
    return
  }

  // Totals (foot band)
  host.appendChild(section('Totals'))
  const totals = el('div', 'space-y-2')
  t.totals.forEach((tot, i) => {
    const r = el('div', 'flex items-center gap-1')
    r.append(
      sel(t.columns.map((c) => [c.field, c.header || c.field] as [string, string]), tot.column, (v) => { const next = [...t.totals]; next[i] = { ...next[i], column: v }; setTotals(t, next); commit() }),
      sel(AGGS, tot.agg, (v) => { const next = [...t.totals]; next[i] = { ...next[i], agg: v as any }; setTotals(t, next); commit() }),
      delBtn(() => { setTotals(t, t.totals.filter((_, j) => j !== i)); commit(); rerender() }),
    )
    totals.appendChild(r)
  })
  if (t.totals.length) totals.appendChild(row('Total label', txt(t.totalsLabel, 'Total', (v) => { setTableOption(t, { totalsLabel: v }); commit() })))
  totals.appendChild(wideBtn('plus', 'Total', () => {
    const firstNum = t.columns.find((c) => c.type === 'number' || c.type === 'currency' || c.cell) ?? t.columns[t.columns.length - 1]
    if (firstNum) { setTotals(t, [...t.totals, { column: firstNum.field, agg: 'sum' }]); commit(); rerender() }
  }))
  host.appendChild(totals)
}
